import { supabase } from '../lib/supabase';

export interface CustomerConversation {
  id: string;
  organization_id: string;
  customer_id: string;
  phone_number: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_count: number;
  assigned_to: string | null;
  status: 'open' | 'snoozed' | 'closed';
  created_at: string;
  updated_at: string;
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    mobile: string | null;
    email: string | null;
  } | null;
}

export interface CustomerMessage {
  id: string;
  conversation_id: string;
  organization_id: string;
  direction: 'inbound' | 'outbound';
  channel: 'sms' | 'note' | 'system';
  body: string;
  from_number: string | null;
  to_number: string | null;
  twilio_sid: string | null;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'received';
  sent_by: string | null;
  read_at: string | null;
  attachments: any[];
  created_at: string;
}

export interface ListConversationsOptions {
  filter?: 'all' | 'unread' | 'mine';
  userId?: string;
  search?: string;
}

export const messagingService = {
  async listConversations(organizationId: string, opts: ListConversationsOptions = {}) {
    let query = supabase
      .from('customer_conversations')
      .select(`
        *,
        customer:customers!customer_conversations_customer_id_fkey (
          id, first_name, last_name, phone, mobile, email
        )
      `)
      .eq('organization_id', organizationId)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (opts.filter === 'unread') query = query.gt('unread_count', 0);
    if (opts.filter === 'mine' && opts.userId) query = query.eq('assigned_to', opts.userId);

    const { data, error } = await query;
    if (error) throw error;
    let rows = (data || []) as unknown as CustomerConversation[];

    if (opts.search) {
      const q = opts.search.toLowerCase();
      rows = rows.filter((c) => {
        const name = `${c.customer?.first_name || ''} ${c.customer?.last_name || ''}`.toLowerCase();
        return (
          name.includes(q) ||
          c.phone_number.includes(q) ||
          (c.last_message_preview || '').toLowerCase().includes(q)
        );
      });
    }
    return rows;
  },

  async getConversation(id: string) {
    const { data, error } = await supabase
      .from('customer_conversations')
      .select(`*, customer:customers!customer_conversations_customer_id_fkey (
        id, first_name, last_name, phone, mobile, email
      )`)
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as unknown as CustomerConversation;
  },

  async getConversationByCustomer(customerId: string, organizationId: string) {
    const { data, error } = await supabase
      .from('customer_conversations')
      .select(`*, customer:customers!customer_conversations_customer_id_fkey (
        id, first_name, last_name, phone, mobile, email
      )`)
      .eq('customer_id', customerId)
      .eq('organization_id', organizationId)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as CustomerConversation | null;
  },

  async getMessages(conversationId: string): Promise<CustomerMessage[]> {
    const { data, error } = await supabase
      .from('customer_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []) as CustomerMessage[];
  },

  async sendMessage(
    customerId: string,
    body: string,
    opts?: { phone?: string; linkUrl?: string; linkLabel?: string }
  ) {
    const { data, error } = await supabase.functions.invoke('send-customer-sms', {
      body: {
        customer_id: customerId,
        body,
        phone: opts?.phone,
        link_url: opts?.linkUrl,
        link_label: opts?.linkLabel,
      },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data as { conversation_id: string; message_id: string; twilio_sid?: string };
  },

  async markAsRead(conversationId: string) {
    const now = new Date().toISOString();
    await supabase
      .from('customer_conversations')
      .update({ unread_count: 0, updated_at: now })
      .eq('id', conversationId);
    await supabase
      .from('customer_messages')
      .update({ read_at: now })
      .eq('conversation_id', conversationId)
      .is('read_at', null)
      .eq('direction', 'inbound');
  },

  subscribeToConversation(
    conversationId: string,
    onMessage: (msg: CustomerMessage) => void
  ) {
    const channel = supabase
      .channel(`conv:${conversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'customer_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => onMessage(payload.new as CustomerMessage)
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },

  subscribeToInbox(
    organizationId: string,
    onChange: () => void
  ) {
    const channel = supabase
      .channel(`inbox:${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'customer_conversations',
          filter: `organization_id=eq.${organizationId}`,
        },
        () => onChange()
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'customer_messages',
          filter: `organization_id=eq.${organizationId}`,
        },
        () => onChange()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },
};
